import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  CccdParsedData,
  countParsedFields,
  hasParsedCccdData,
  normalizeDateForInput,
} from '../utils/cccdOcr';
import { parseCccdQrPayload } from '../utils/cccdQr';
import { decodeQrFromImageFile } from '../utils/decodeQrFromImage';

interface CccdScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScanned: (data: CccdParsedData) => void;
  /** Tự mở ứng dụng máy ảnh gốc khi modal hiển thị */
  autoOpenCamera?: boolean;
  /** Tự mở thư viện ảnh khi modal hiển thị */
  autoOpenGallery?: boolean;
}

const FILE_READER_ID = 'cccd-qr-reader-file';

const CccdScannerModal: React.FC<CccdScannerModalProps> = ({
  isOpen,
  onClose,
  onScanned,
  autoOpenCamera = false,
  autoOpenGallery = false,
}) => {
  const [parsedResult, setParsedResult] = useState<CccdParsedData | null>(null);
  const [scannerError, setScannerError] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const autoOpenedRef = useRef(false);
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = null;
  }, []);

  const setPreview = useCallback(
    (url: string | null) => {
      revokePreviewUrl();
      if (url?.startsWith('blob:')) {
        previewUrlRef.current = url;
      }
      setImagePreview(url);
    },
    [revokePreviewUrl],
  );

  const resetState = useCallback(() => {
    autoOpenedRef.current = false;
    setParsedResult(null);
    setScannerError('');
    setIsProcessingFile(false);
    setPreview(null);
  }, [setPreview]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const openNativeCamera = useCallback(() => {
    cameraInputRef.current?.click();
  }, []);

  const applyParsed = useCallback((parsed: CccdParsedData) => {
    if (!hasParsedCccdData(parsed)) {
      setScannerError(
        'Mã QR không chứa thông tin CCCD hợp lệ. Hãy chụp rõ mặt trước thẻ CCCD gắn chip bằng ứng dụng máy ảnh.',
      );
      return;
    }
    setParsedResult(parsed);
    onScannedRef.current(parsed);
    if (countParsedFields(parsed) < 5) {
      setScannerError(
        `Đã đọc ${countParsedFields(parsed)}/5 trường từ QR. Kiểm tra thông tin đã điền, quét lại nếu còn thiếu.`,
      );
    } else {
      setScannerError('');
    }
  }, []);

  const handleApply = () => {
    if (!parsedResult || !hasParsedCccdData(parsedResult)) return;
    onScannedRef.current(parsedResult);
    handleClose();
  };

  const decodeQrFromFile = useCallback(
    async (file: File) => {
      setIsProcessingFile(true);
      setScannerError('');
      setParsedResult(null);

      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      try {
        const decodedText = await decodeQrFromImageFile(file, FILE_READER_ID);
        applyParsed(parseCccdQrPayload(decodedText));
      } catch (error: unknown) {
        console.error('QR decode failed:', error);
        setScannerError(
          'Không đọc được QR trong ảnh. Hãy chụp lại bằng máy ảnh gốc, đảm bảo mã QR mặt trước CCCD rõ, gần và không bị lóa.',
        );
      } finally {
        setIsProcessingFile(false);
      }
    },
    [applyParsed, setPreview],
  );

  const handleImageInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await decodeQrFromFile(file);
  };

  const handleRetry = useCallback(() => {
    resetState();
    window.setTimeout(() => {
      openNativeCamera();
    }, 200);
  }, [openNativeCamera, resetState]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useLayoutEffect(() => {
    if (!isOpen || autoOpenedRef.current) return;
    if (autoOpenCamera) {
      autoOpenedRef.current = true;
      openNativeCamera();
      return;
    }
    if (autoOpenGallery) {
      autoOpenedRef.current = true;
      galleryInputRef.current?.click();
    }
  }, [autoOpenCamera, autoOpenGallery, isOpen, openNativeCamera]);

  useEffect(
    () => () => {
      revokePreviewUrl();
    },
    [revokePreviewUrl],
  );

  const renderField = (label: string, value?: string) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={`text-sm mt-0.5 break-words ${value ? 'text-black font-medium' : 'text-gray-400 italic'}`}
      >
        {value || 'Không đọc được'}
      </p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center bg-black/60 p-0 sm:p-4"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="relative bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg max-h-[min(92dvh,calc(100vh-1rem))] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cccd-scanner-title"
      >
        <div className="shrink-0 border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2
              id="cccd-scanner-title"
              className="text-base sm:text-lg font-semibold text-black flex items-center gap-2"
            >
              <i className="fas fa-qrcode text-[#C4A35A] shrink-0"></i>
              <span>Quét QR CCCD</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              {parsedResult
                ? 'Kiểm tra thông tin trước khi điền vào hợp đồng'
                : 'Dùng ứng dụng máy ảnh gốc của điện thoại để chụp rõ mã QR trên CCCD'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isProcessingFile}
            className="shrink-0 text-gray-400 hover:text-black transition-colors p-1 disabled:opacity-50"
            aria-label="Đóng"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5 space-y-4">
          {!parsedResult && (
            <>
              <div className="relative overflow-hidden rounded-xl border border-[#E7D8B2] bg-[#FFFCF5]">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Ảnh CCCD đã chụp"
                    className="w-full max-h-72 object-contain bg-black"
                  />
                ) : (
                  <div className="flex min-h-[220px] flex-col items-center justify-center px-6 py-8 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F7F1E3] text-[#C4A35A]">
                      <i className="fas fa-camera text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium text-black">Chụp bằng ứng dụng máy ảnh</p>
                    <p className="mt-2 text-xs text-gray-500">
                      Bấm &quot;Mở máy ảnh&quot; để dùng camera gốc — ảnh sẽ nét hơn
                    </p>
                  </div>
                )}

                {isProcessingFile && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 text-white">
                    <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                    <p className="text-sm font-medium">Đang đọc mã QR...</p>
                  </div>
                )}
              </div>

              <ul className="text-xs sm:text-sm text-gray-500 space-y-1 list-disc pl-4">
                <li>
                  Quét mã QR ở <strong>mặt trước</strong> CCCD gắn chip
                </li>
                <li>
                  Dùng <strong>ứng dụng máy ảnh gốc</strong> của điện thoại (không quét trong app)
                </li>
                <li>Hoặc chọn ảnh CCCD đã chụp sẵn từ thư viện</li>
              </ul>
            </>
          )}

          {parsedResult && (
            <div className="space-y-2">
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Ảnh CCCD"
                  className="w-full max-h-36 object-contain rounded-lg border border-gray-200 bg-black"
                />
              )}
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-black">Thông tin điền vào hợp đồng</h3>
                <span className="text-xs font-medium text-[#8C5226] bg-[#F7F1E3] px-2 py-0.5 rounded-full">
                  {countParsedFields(parsedResult)}/5 trường
                </span>
              </div>
              {renderField('Họ và tên người thuê', parsedResult.name)}
              {renderField('Số CCCD', parsedResult.cccd)}
              {renderField(
                'Ngày sinh',
                parsedResult.dateOfBirth
                  ? normalizeDateForInput(parsedResult.dateOfBirth) || parsedResult.dateOfBirth
                  : undefined,
              )}
              {renderField('Địa chỉ thường trú', parsedResult.address)}
              {renderField('Số điện thoại', parsedResult.phone)}
            </div>
          )}

          {scannerError && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                parsedResult
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              {scannerError}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {parsedResult ? (
            <>
              <button
                type="button"
                onClick={() => {
                  void handleRetry();
                }}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Chụp lại
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="flex-1 rounded-lg bg-[#C4A35A] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-black"
              >
                Dùng thông tin này
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={openNativeCamera}
                disabled={isProcessingFile}
                className="flex-1 rounded-lg bg-[#C4A35A] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-black disabled:opacity-50"
              >
                {isProcessingFile ? 'Đang đọc QR...' : 'Mở máy ảnh'}
              </button>
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={isProcessingFile}
                className="flex-1 rounded-lg border border-[#C4A35A] bg-white px-4 py-2.5 text-sm font-medium text-[#8C5226] transition hover:bg-[#F7F1E3] disabled:opacity-50"
              >
                Chọn ảnh
              </button>
            </>
          )}

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(event) => {
              void handleImageInput(event);
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              void handleImageInput(event);
            }}
          />
          <div
            id={FILE_READER_ID}
            aria-hidden="true"
            className="pointer-events-none absolute -left-[9999px] top-0 h-[300px] w-[300px] overflow-hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default CccdScannerModal;
